import videojs from 'video.js';

export default class HlsPlayerManager {
  private videoJSPlayer: videojs.Player;
  private overlayPlayer: videojs.Player | null = null;
  private resizeListener?: () => void;

  // Originale Overlay-Größe (16:9)
  private readonly originalWidth = 480;
  private readonly originalHeight = 270;
  private readonly ratio = 480 / 270; // ca. 1.7778

  constructor(videoJSPlayer: videojs.Player) {
    this.videoJSPlayer = videoJSPlayer;
  }

  /**
   * Zeigt den HLS-Player als Overlay an – wenn das Overlay bereits existiert, wird es entfernt.
   * @param url Die URL des HLS-Streams.
   */
  public showHlsPlayer(url: string): void {
    const container = this.videoJSPlayer.el();
    const existingOverlay = container.querySelector("#hls-overlay");

    if (existingOverlay) {
      this.destroy();
      return;
    }

    // Overlay-Container erstellen
    const overlayContainer = document.createElement("div");
    overlayContainer.id = "hls-overlay";
    overlayContainer.style.position = "absolute";
    overlayContainer.style.zIndex = "999";
    overlayContainer.style.cursor = "move"; // verschiebbar

    // Container dem Video.js-Player hinzufügen
    container.appendChild(overlayContainer);

    // Größe und Position initial setzen
    this.updateOverlaySize(overlayContainer);

    // Resize-Listener hinzufügen
    this.resizeListener = () => this.updateOverlaySize(overlayContainer);
    window.addEventListener("resize", this.resizeListener);

    // Video-Element ohne Controls
    const videoEl = document.createElement("video");
    videoEl.className = "video-js vjs-default-skin";
    videoEl.setAttribute("preload", "auto");
    videoEl.style.width = "100%";
    videoEl.style.height = "100%";
    overlayContainer.appendChild(videoEl);

    // Drag-and-Drop inkl. Touch-Support
    this.enableDrag(overlayContainer);

    // Video.js-Player initialisieren
    this.overlayPlayer = videojs(videoEl, {
      sources: [{
        src: url,
        type: "application/x-mpegURL"
      }],
      controls: false,
      fluid: true
    });
  }

  /**
   * Passt Größe und Position des Overlays an.
   * Die originale Größe (480×270, 16:9) bleibt erhalten – nur bei zu wenig Platz wird proportional skaliert.
   * @param overlay Das Overlay-HTMLElement.
   */
  private updateOverlaySize(overlay: HTMLElement): void {
    // Hole den Video.js-Player (als Referenz für maximale Höhe)
    const player = document.querySelector(".video-js") as HTMLElement;
    if (!player) return;
    const playerHeight = player.clientHeight;
    const maxBoxHeight = playerHeight / 2; // maximal 50% der Player-Höhe

    if (window.matchMedia("(max-width: 768px)").matches) {
      // Mobile: Overlay soll im oberen Bereich mittig erscheinen
      const screenHeight = window.innerHeight;
      // Ausgangswert: 1/3 der Bildschirmhöhe – aber nicht größer als originalHeight oder maxBoxHeight
      let boxHeight = Math.min(screenHeight / 3, this.originalHeight, maxBoxHeight);
      const boxWidth = boxHeight * this.ratio;
      overlay.style.top = "0";
      overlay.style.left = "50%";
      overlay.style.width = boxWidth + "px";
      overlay.style.height = boxHeight + "px";
      overlay.style.transform = "translateX(-50%)"; // horizontal zentriert
    } else {
      // Desktop: Bevorzugt in der rechten oberen Ecke (10px Abstand)
      // Starte mit den originalen Maßen…
      let width = this.originalWidth;
      let height = this.originalHeight;

      // Falls die maximale Höhe des Players kleiner ist als die Originalhöhe, skaliere herunter
      if (height > maxBoxHeight) {
        const factor = maxBoxHeight / height;
        height = maxBoxHeight;
        width = width * factor;
      }

      // Optional: Minimale bzw. maximale Breiten (wie im alten Code)
      const minWidth = 400;
      const maxWidth = 480;
      if (width < minWidth) {
        width = minWidth;
        height = width / this.ratio;
      }
      if (width > maxWidth) {
        width = maxWidth;
        height = width / this.ratio;
      }

      overlay.style.top = "10px";
      overlay.style.right = "10px";
      overlay.style.left = "auto";
      overlay.style.width = width + "px";
      overlay.style.height = height + "px";
      overlay.style.transform = "none";
    }
  }

  /**
   * Ermöglicht das Verschieben des Elements per Drag-and-Drop, inkl. Touch-Support.
   * @param element Das verschiebbare HTMLElement.
   */
  private enableDrag(element: HTMLElement): void {
    let isDragging = false;
    let offsetX = 0;
    let offsetY = 0;

    // Maus-Events
    element.addEventListener("mousedown", (e: MouseEvent) => {
      isDragging = true;
      offsetX = e.clientX - element.getBoundingClientRect().left;
      offsetY = e.clientY - element.getBoundingClientRect().top;
    });
    document.addEventListener("mousemove", (e: MouseEvent) => {
      if (!isDragging) return;
      element.style.left = `${e.clientX - offsetX}px`;
      element.style.top = `${e.clientY - offsetY}px`;
    });
    document.addEventListener("mouseup", () => {
      isDragging = false;
    });

    // Touch-Events
    element.addEventListener("touchstart", (e: TouchEvent) => {
      if (e.touches.length === 1) {
        isDragging = true;
        const touch = e.touches[0];
        offsetX = touch.clientX - element.getBoundingClientRect().left;
        offsetY = touch.clientY - element.getBoundingClientRect().top;
      }
    }, { passive: false });
    document.addEventListener("touchmove", (e: TouchEvent) => {
      if (!isDragging || e.touches.length !== 1) return;
      e.preventDefault();
      const touch = e.touches[0];
      element.style.left = `${touch.clientX - offsetX}px`;
      element.style.top = `${touch.clientY - offsetY}px`;
    }, { passive: false });
    document.addEventListener("touchend", () => {
      isDragging = false;
    });
  }

  /**
   * Zerstört den HLS-Player-Overlay und entfernt ihn aus dem DOM.
   */
  public destroy(): void {
    if (this.overlayPlayer) {
      this.overlayPlayer.dispose();
      this.overlayPlayer = null;
    }
    const container = this.videoJSPlayer.el();
    const overlay = container.querySelector("#hls-overlay");
    if (overlay) {
      overlay.remove();
    }
    if (this.resizeListener) {
      window.removeEventListener("resize", this.resizeListener);
      this.resizeListener = undefined;
    }
  }
}
